import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { del, put } from "src/utils/networkUtils";

const MAX_PLAYER_PRICE = 10;

// Availability overrides are a deliberate manual decision that wins over the
// OPTA feed and survives the hourly sync until reverted. "unknown"/null fall
// back to "available" so the control always shows a sensible default.
const OVERRIDE_STATUSES = ["available", "injured", "suspended"];
const normalizeOverrideStatus = (value) =>
  OVERRIDE_STATUSES.includes(value) ? value : "available";

// Forwards are stored as "STR" - that is what the OPTA import writes and what
// fans see in the game. Seeded rows use "FWD" for the same position, so fold it
// in. An unrecognised value maps to "" so the select stays empty rather than
// silently rewriting the player's position.
const POSITIONS = ["GK", "DEF", "MID", "STR"];

const normalizePosition = (position) => {
  const normalized = position?.trim().toUpperCase();
  if (normalized === "FWD") return "STR";
  return POSITIONS.includes(normalized) ? normalized : "";
};

const EditPlayerModal = ({ open, player, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    matchName: "",
    price: "",
    position: normalizePosition(player?.position),
    isVisible: true,
    hasLeft: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [nameCopied, setNameCopied] = useState(false);
  const [currentPlayerId, setCurrentPlayerId] = useState(null);
  const [override, setOverride] = useState({
    status: "available",
    reason: "",
    injuryType: "",
    expectedReturn: "",
  });
  // null | "apply" | "revert" - tracks which override action is in flight.
  const [overrideLoading, setOverrideLoading] = useState(null);
  const [overrideError, setOverrideError] = useState(null);

  const currentPosition = normalizePosition(player?.position);

  useEffect(() => {
    if (player?.id !== currentPlayerId) {
      setCurrentPlayerId(player?.id || null);
      setFormData({
        firstName: player?.firstName || "",
        lastName: player?.lastName || "",
        matchName: player?.matchName || "",
        price: player?.price?.toString() || "0",
        position: normalizePosition(player?.position),
        isVisible:
          player?.isVisible !== false && player?.isVisible !== undefined,
        hasLeft: player?.hasLeft === true,
      });
      setOverride({
        status: normalizeOverrideStatus(player?.availabilityDisplay),
        reason: "",
        injuryType: "",
        expectedReturn: "",
      });
      setOverrideError(null);
    }
  }, [player, currentPlayerId]);

  useEffect(() => {
    if (!open) {
      setNameCopied(false);
    }
  }, [open]);

  const validateForm = () => {
    const newErrors = {};
    const price = parseFloat(formData.price);

    if (isNaN(price) || price < 0) {
      newErrors.price = "Price must be 0 or greater";
    } else if (price > MAX_PLAYER_PRICE) {
      newErrors.price = `Price cannot exceed £${MAX_PLAYER_PRICE.toFixed(1)}m`;
    } else if (!/^\d+(?:\.\d)?$/.test(formData.price.trim())) {
      newErrors.price = "Price must have no more than one decimal place";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCopyDreamTeamName = () => {
    if (player?.dreamTeamName) {
      navigator.clipboard.writeText(player.dreamTeamName);
      setNameCopied(true);
      setTimeout(() => setNameCopied(false), 2000);
    }
  };

  const handleApplyDreamTeamPrice = () => {
    if (
      player?.dreamTeamPrice !== null &&
      player?.dreamTeamPrice !== undefined
    ) {
      setFormData((prev) => ({
        ...prev,
        price: player.dreamTeamPrice.toString(),
      }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        matchName: formData.matchName.trim(),
        price: parseFloat(formData.price),
        isVisible: formData.isVisible,
        hasLeft: formData.hasLeft,
      };

      // The backend evaluates the current gameweek/matchday whenever position
      // is present, so only send it when the value actually changed.
      if (formData.position && formData.position !== currentPosition) {
        payload.position = formData.position;
      }

      const response = await put(`/admin/players/${player.id}`, payload);
      onSave(response?.data || response);
      onClose();
    } catch (error) {
      console.error("Failed to update player:", error);
      setErrors({
        submit: error.response?.data?.message || "Failed to update player",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApplyOverride = async () => {
    setOverrideLoading("apply");
    setOverrideError(null);
    try {
      const payload = {
        status: override.status,
        reason: override.reason.trim() || null,
      };
      if (override.status === "injured") {
        payload.injuryType = override.injuryType.trim() || null;
        payload.expectedReturn = override.expectedReturn.trim() || null;
      }
      const response = await put(
        `/admin/players/${player.id}/availability-override`,
        payload,
      );
      onSave(response?.data || response);
      onClose();
    } catch (error) {
      console.error("Failed to apply availability override:", error);
      setOverrideError(
        error.response?.data?.message || "Failed to apply override",
      );
    } finally {
      setOverrideLoading(null);
    }
  };

  const handleRevertOverride = async () => {
    setOverrideLoading("revert");
    setOverrideError(null);
    try {
      const response = await del(
        `/admin/players/${player.id}/availability-override`,
      );
      onSave(response?.data || response);
      onClose();
    } catch (error) {
      console.error("Failed to revert availability override:", error);
      setOverrideError(
        error.response?.data?.message || "Failed to revert override",
      );
    } finally {
      setOverrideLoading(null);
    }
  };

  if (!player) return null;

  const hasDreamTeamName = player.dreamTeamName;
  const hasDreamTeamPrice =
    player.dreamTeamPrice !== null && player.dreamTeamPrice !== undefined;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Player</DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
          {hasDreamTeamName && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 2,
                bgcolor: "action.hover",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Dream Team Name:
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body1" fontWeight="medium">
                    {player.dreamTeamName}
                  </Typography>
                  <Tooltip title={nameCopied ? "Copied!" : "Copy to clipboard"}>
                    <IconButton
                      size="small"
                      onClick={handleCopyDreamTeamName}
                      color={nameCopied ? "success" : "default"}
                    >
                      {nameCopied ? (
                        <CheckIcon fontSize="small" />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          )}

          <Typography variant="subtitle2" color="text.secondary">
            Player Name
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                error={!!errors.firstName}
                helperText={errors.firstName}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Match Name"
                value={formData.matchName}
                onChange={(e) => handleChange("matchName", e.target.value)}
                placeholder="N/A"
                helperText="Most commonly known player name"
                fullWidth
              />
            </Grid>
          </Grid>

          <TextField
            label="Price"
            type="number"
            value={formData.price}
            onChange={(e) => handleChange("price", e.target.value)}
            error={!!errors.price}
            helperText={
              errors.price ||
              `Enter price between £0.0m and £${MAX_PLAYER_PRICE.toFixed(1)}m`
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">£</InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      m
                    </Typography>
                    {hasDreamTeamPrice && (
                      <Tooltip title="Use Dream Team price">
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={handleApplyDreamTeamPrice}
                          sx={{
                            minWidth: "auto",
                            px: 1,
                            py: 0.25,
                            fontSize: "0.7rem",
                          }}
                        >
                          Use £{player.dreamTeamPrice}m
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                </InputAdornment>
              ),
            }}
            inputProps={{
              step: 0.1,
              min: 0,
              max: 5,
            }}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel id="position-label">Position</InputLabel>
            <Select
              labelId="position-label"
              value={formData.position}
              label="Position"
              onChange={(e) => handleChange("position", e.target.value)}
            >
              {POSITIONS.map((position) => (
                <MenuItem key={position} value={position}>
                  {position}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Overrides the position from the OPTA feed. Only changeable during
              an upcoming/open gameweek, or for matchday-based tournaments, an
              upcoming matchday.
            </FormHelperText>
          </FormControl>

          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2">Availability override</Typography>
              <Typography variant="caption" color="text.secondary">
                A manual decision that wins over the OPTA injuries/suspensions
                feed and survives the hourly sync until reverted. Use Available
                to clear a stale OPTA flag, or Injured/Suspended to flag a player
                the feed missed.
              </Typography>
            </Box>

            <FormControl fullWidth>
              <InputLabel id="override-status-label">Status</InputLabel>
              <Select
                labelId="override-status-label"
                value={override.status}
                label="Status"
                onChange={(e) =>
                  setOverride((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="injured">Injured</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Reason (optional)"
              value={override.reason}
              onChange={(e) =>
                setOverride((prev) => ({ ...prev, reason: e.target.value }))
              }
              placeholder="e.g. OPTA showing a stale injury, player is fit"
              fullWidth
            />

            {override.status === "injured" && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Injury type (optional)"
                    value={override.injuryType}
                    onChange={(e) =>
                      setOverride((prev) => ({
                        ...prev,
                        injuryType: e.target.value,
                      }))
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Expected return (optional)"
                    type="date"
                    value={override.expectedReturn}
                    onChange={(e) =>
                      setOverride((prev) => ({
                        ...prev,
                        expectedReturn: e.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
              </Grid>
            )}

            {overrideError && (
              <Typography color="error" variant="body2">
                {overrideError}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleApplyOverride}
                disabled={overrideLoading !== null}
                startIcon={
                  overrideLoading === "apply" ? (
                    <CircularProgress size={18} />
                  ) : null
                }
              >
                {overrideLoading === "apply" ? "Applying..." : "Apply override"}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleRevertOverride}
                disabled={overrideLoading !== null}
                startIcon={
                  overrideLoading === "revert" ? (
                    <CircularProgress size={18} />
                  ) : null
                }
              >
                {overrideLoading === "revert"
                  ? "Reverting..."
                  : "Revert to OPTA feed"}
              </Button>
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isVisible}
                onChange={(e) => handleChange("isVisible", e.target.checked)}
              />
            }
            label="Visible to Users"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.hasLeft}
                onChange={(e) => handleChange("hasLeft", e.target.checked)}
              />
            }
            label="Has Left League"
          />

          {errors.submit && (
            <Typography color="error" variant="body2">
              {errors.submit}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPlayerModal;
